import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { inviteUser, inviteUsersBulk } from '@/store/auth/auth.api';
import { UserRole } from '@/utils/roles';
import { toast } from '@/utils/toast';

import {
  acceptInvitation,
  createInviteCode,
  deleteInvite,
  generateInviteLink,
  getInviteById,
  getInviteCodes,
  getInvites,
  getSeatAvailability,
  invite,
  joinByInviteCode,
} from './invites.api';
import type {
  CreateInvitation,
  GenerateInviteLinkRequest,
  InviteCodeRole,
  SingleInvite,
} from './invites.types';

export interface PendingGuestInvite {
  id: string;
  /** Every live row for this invitee: withdrawing only `id` would leave them invited. */
  ids: string[];
  /** Set for someone who already has an account — their invite carries no contact. */
  personId?: string;
  contact: string;
  name: string;
  message?: string;
  /** When it was sent. */
  createdAt: string;
}

/**
 * The guests invited to an event who have not answered yet: the invite row is
 * deleted on accept, at which point the person is a participant instead. The
 * backend allows duplicate invite rows for the same invitee+event (no unique
 * constraint), so the same person can come back twice — one entry per person/contact,
 * holding all of their row ids.
 */
export const pendingGuestInvites = (
  invites: SingleInvite[] | undefined,
  participantIds: Set<string>,
): PendingGuestInvite[] => {
  const byInvitee = new Map<string, PendingGuestInvite>();
  for (const i of invites ?? []) {
    if (i.role !== UserRole.GUEST || participantIds.has(i.person_id)) continue;
    const key = i.person_id || i.contact;
    const seen = byInvitee.get(key);
    if (seen) {
      seen.ids.push(i.id);
      continue;
    }
    byInvitee.set(key, {
      id: i.id,
      ids: [i.id],
      personId: i.person_id || undefined,
      contact: i.contact,
      name: i.name,
      message: i.message,
      createdAt: i.created_at,
    });
  }
  return [...byInvitee.values()];
};

export const useCreateInvitationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (i: CreateInvitation) => invite(i),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['invites-list'] });
    },
  });
};

export interface GuestInvitee {
  name: string;
  contact: string;
  /** Set for a contact picked from Contacts: they already have an account. */
  personId?: string;
}

/**
 * Invites guests to an event. A contact picked from Contacts is invited by person id, like a past
 * guest: auth matches an account only on its exact login, and the phone or email a contact lists
 * is often not it — the invitee then got a sign-up SMS/email and no in-app invitation, while the
 * sender saw them as Invited. A guest typed in by contact still goes through auth, which signs up
 * a newcomer or finds the account.
 */
export const useInviteGuestsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      invitees,
      vessel,
      event,
    }: {
      invitees: GuestInvitee[];
      vessel: { id: string; vessel_name?: string };
      event: { id: string; name: string };
    }) => {
      const byContact = invitees.filter(({ personId }) => !personId);
      await Promise.all([
        ...(byContact.length > 0
          ? [
              inviteUsersBulk(
                byContact.map(({ contact, name }) => ({ contact, name })),
                UserRole.GUEST,
                vessel.id,
                'vessel',
                vessel.vessel_name,
                event,
              ),
            ]
          : []),
        ...invitees.flatMap(({ personId, name }) =>
          personId
            ? [
                invite({
                  personId,
                  name,
                  role: UserRole.GUEST,
                  serviceId: vessel.id,
                  serviceType: 'vessel',
                  vesselName: vessel.vessel_name ?? '',
                  event,
                }),
              ]
            : [],
        ),
      ]);
    },
    onSettled() {
      // Settled, not success: one invitee failing still leaves the others invited.
      queryClient.invalidateQueries({ queryKey: ['invites-list'] });
    },
  });
};

export const useSingleInviteQuery = (id: string) =>
  useQuery({
    queryKey: ['invites', id],
    queryFn: () => getInviteById(id),
    enabled: !!id,
  });

export const useAcceptInviteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => acceptInvitation(id),
    onSuccess(_, id) {
      queryClient.invalidateQueries({ queryKey: ['invites', id] });
    },
  });
};

export const useInvitesQuery = (params: {
  person_id?: string;
  service_id?: string;
  event_id?: string;
}) =>
  useQuery({
    queryKey: ['invites-list', params.person_id, params.service_id, params.event_id],
    queryFn: () => getInvites({ page: 1, limit: 100, ...params }),
    enabled: !!(params.person_id || params.service_id || params.event_id),
  });

/**
 * The invites on one event. Both ids are required: without service_id the backend
 * scopes the list to the caller's own invites, so the owner would always see none.
 * Shares useInvitesQuery's key so the cache and its invalidation are the same.
 */
export const useEventInvitesQuery = (eventId?: string, vesselId?: string) =>
  useQuery({
    queryKey: ['invites-list', undefined, vesselId, eventId],
    queryFn: () => getInvites({ page: 1, limit: 100, event_id: eventId, service_id: vesselId }),
    enabled: !!eventId && !!vesselId,
  });

export const useDeclineInviteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => deleteInvite(id, reason),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: ['invites-list'] });
      queryClient.invalidateQueries({ queryKey: ['invites', id] });
    },
  });
};

export const inviteCodesKey = (vesselId: string | undefined) => ['invite-codes', vesselId] as const;

/** The vessel's pending codes. Only its owner group and captain may read them. */
export const useInviteCodesQuery = (vesselId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: inviteCodesKey(vesselId),
    queryFn: () => getInviteCodes(vesselId!),
    enabled: enabled && !!vesselId,
  });

export const useCreateInviteCodeMutation = (vesselId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (role: InviteCodeRole) => createInviteCode(vesselId, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: inviteCodesKey(vesselId) }),
  });
};

export const useRevokeInviteCodeMutation = (vesselId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => deleteInvite(inviteId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: inviteCodesKey(vesselId) }),
  });
};

export const useJoinByInviteCodeMutation = () =>
  useMutation({
    mutationFn: (code: string) => joinByInviteCode(code),
  });

/**
 * There is no resend endpoint: an invitation is sent when it is created. So a resend is a
 * fresh invitation — a new in-app notification, or a new email/SMS for someone without an
 * account, and a new INVITE_TTL — after which the old rows are withdrawn. Leaving them would
 * hand the invitee two invitations that cancel and decline each only half-remove.
 * Withdrawing notifies nobody, and an emailed link already sent keeps working: sign-up takes
 * every invitation to the address.
 */
export const useResendInviteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      pending,
      role,
      roleName,
      event,
      vesselId,
      vesselName,
    }: {
      pending: PendingGuestInvite;
      role: string;
      roleName?: string;
      event?: { id: string; name: string };
      vesselId: string;
      vesselName?: string;
    }) => {
      if (pending.personId) {
        await invite({
          personId: pending.personId,
          role,
          roleName,
          name: pending.name,
          serviceId: vesselId,
          serviceType: 'vessel',
          vesselName: vesselName ?? '',
          event,
          message: pending.message,
        });
      } else {
        await inviteUser(
          pending.contact,
          pending.name,
          role,
          vesselId,
          'vessel',
          vesselName,
          roleName,
          event,
          pending.message,
        );
      }
      // ponytail: a withdrawal that fails leaves a duplicate row; cancel still takes all of them.
      const withdrawn = await Promise.allSettled(pending.ids.map((id) => deleteInvite(id)));
      withdrawn.forEach((r) => {
        if (r.status === 'rejected') console.error('Withdraw old invitation error:', r.reason);
      });
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['invites-list'] });
    },
  });
};

/**
 * Cancel and Resend for a pending guest invitation, wherever one is listed. Without an
 * `event` it is an invitation onto the vessel itself.
 */
export const useGuestInviteActions = () => {
  // Same DELETE as an invitee's decline; from the sender it is a withdrawal and notifies nobody.
  const { mutateAsync: withdraw } = useDeclineInviteMutation();
  const { mutateAsync: resendInvite, isPending: isResending } = useResendInviteMutation();

  const cancel = async (invite: PendingGuestInvite) => {
    try {
      await Promise.all(invite.ids.map((id) => withdraw({ id })));
      toast.success('Invitation cancelled');
    } catch (error) {
      console.error('Cancel invitation error:', error);
      toast.error('Couldn’t cancel the invitation. Please try again.');
    }
  };

  const resend = async (
    invite: PendingGuestInvite,
    vessel: { id: string; vessel_name?: string },
    event?: { id: string; name: string },
  ) => {
    try {
      await resendInvite({
        pending: invite,
        role: UserRole.GUEST,
        event,
        vesselId: vessel.id,
        vesselName: vessel.vessel_name,
      });
      toast.success('Invitation resent');
    } catch (error) {
      console.error('Resend invitation error:', error);
      toast.error('Couldn’t resend the invitation. Please try again.');
    }
  };

  return { cancel, resend, isResending };
};

export const useGenerateInviteLink = () =>
  useMutation({
    mutationFn: (body: GenerateInviteLinkRequest) => generateInviteLink(body),
  });

/** The seat somebody invited in `role` would take on the vessel; asked only once a role is picked. */
export const useSeatAvailabilityQuery = (serviceId?: string, role?: string) =>
  useQuery({
    queryKey: ['seat-availability', serviceId, role],
    queryFn: () => getSeatAvailability(serviceId!, role!),
    enabled: !!serviceId && !!role,
  });
